import { getCalculatedProjectValueInPercent } from '@app/shared/formatters/number-value-formatter';
import { getWaterfallCaseYearInstrumentInfusionTransactionSummary } from '@app/core/store/pwerm2-calculation-slice-selectors';
import CellValue from '@app/shared/components/cell-value/CellValue';
import {
  enteredPercentageViewRules,
  amountRules,
  decimalCalculatedViewRules,
} from '@app/shared/components/cell-value/CellValueConfigurations';
import { TransactionType } from '@app/shared/models/contracts/enums/shared-enums';
import { l } from '@locale/setupI18n';
import { FC } from 'react';
import TableSectionValuesPerForecast from '../../components/TableSectionValuesPerForecast';
import TableSpacerRow from '@app/shared/components/table/TableSpacerRow';
import { ValuationMovement } from './ValuationMovement';
import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { InstrumentDefinitionWithId } from '@app/core/store/capital-structure-slice-selectors';
import { Pwerm2CalculationWaterfallCaseYearInstrumentMovementTransactionDto } from '@app/shared/models/contracts/pwerm2-calculation-results-dto';
import { CaseDto, ForecastDto } from '@app/shared/models/contracts/project-dto';
import classNames from 'classnames';
import styles from '../../components/table-section-values.module.scss';
import { CashFlowType, useTotalPvModal } from '../pwerm-waterfall-utils/useTotalPvModal';
import { maxNumberOfTransactionsInYear } from './utils/transactionUtils';
import { TotalPvOfInterimCashFlowsModal } from './TotalPvOfInterimCashFlowsModal';

export const InstrumentInfusionCashFlows: FC<{
  instrument: InstrumentDefinitionWithId;
  infusionTransactions: Record<
    string,
    Record<string, Pwerm2CalculationWaterfallCaseYearInstrumentMovementTransactionDto[]>
  >;
  columnsCount: number;
}> = ({ instrument, infusionTransactions, columnsCount }) => {
  const results = useAppSelector((state) => state.pwerm2Calculation.calculatedResults);
  const maxDataColspanCount = columnsCount - 1;
  const modalHandler = useTotalPvModal(CashFlowType.Infusion, results);

  const maxNumberOfInfusions = maxNumberOfTransactionsInYear(infusionTransactions);
  const hasMultipleInfusionTransactions = maxNumberOfInfusions > 1;

  const getTotalPvOfInfusionsValueCell = (forecast: ForecastDto, caseItem: CaseDto) => {
    const totalPvValue = getWaterfallCaseYearInstrumentInfusionTransactionSummary(
      results,
      caseItem.caseId,
      forecast.forecastId,
      instrument.instrumentId,
      (transactionSummary) => transactionSummary.totalPvOfInfusionMovement
    );

    const isClickable = Boolean(totalPvValue);

    const handleClick = () => {
      if (isClickable) {
        modalHandler.handleCellClick(caseItem.caseId, forecast.forecastId, instrument);
      }
    };

    return (
      <div onClick={handleClick}>
        <CellValue
          {...amountRules}
          className={classNames('table-primary__cell--strong', {
            [styles['clickable-cell']]: isClickable,
          })}
          value={totalPvValue}
          dataTestId={`total-pv-interim-cell-${caseItem.caseId}-${forecast.forecastId}`}
        />
      </div>
    );
  };

  return (
    <>
      <tr>
        <th className="table-primary__cell--strong">{l('_Infusions')}</th>
        <th className="table-primary__cell--strong" colSpan={maxDataColspanCount} />
      </tr>
      <ValuationMovement
        instrument={instrument}
        transactionsByCaseAndYear={infusionTransactions}
        transactionType={TransactionType.Infusion}
        rowLabel={l('_Infusion')}
        valueSelector={(transaction) => transaction?.amount}
        cellValueFormattingRules={amountRules}
      />
      {hasMultipleInfusionTransactions && (
        <TableSectionValuesPerForecast
          title={l('_TotalInfusion')}
          renderSpacer={false}
          additionalTitleClassNames="table-primary__cell--strong"
          renderCell={({ forecast, caseItem }) => {
            const transactions: Pwerm2CalculationWaterfallCaseYearInstrumentMovementTransactionDto[] =
              infusionTransactions[caseItem.caseId][forecast.forecastYear] || [];

            if (transactions.length === 0) {
              return null;
            }

            const totalValue = transactions.reduce((sum, transaction) => {
              return sum + transaction.amount;
            }, 0);
            return (
              <CellValue
                {...amountRules}
                className="table-primary__cell--strong"
                value={totalValue}
                dataTestId={`total-infusion-cell-${caseItem.caseId}-${forecast.forecastId}`}
              />
            );
          }}
        />
      )}
      <TableSectionValuesPerForecast
        title={l('_CostOfEquity')}
        renderSpacer={false}
        renderCell={({ forecast, caseItem }) => {
          const value = getCalculatedProjectValueInPercent(
            getWaterfallCaseYearInstrumentInfusionTransactionSummary(
              results,
              caseItem.caseId,
              forecast.forecastId,
              instrument.instrumentId,
              (transactionSummary) =>
                transactionSummary.infusionInterimTransactionsDiscountRate ??
                transactionSummary.originalInterimTransactionsDiscountRate
            )
          );
          return (
            <CellValue
              {...enteredPercentageViewRules}
              value={value}
              dataTestId={`discount-rate-cell-${caseItem.caseId}-${forecast.forecastId}`}
            />
          );
        }}
      />
      {hasMultipleInfusionTransactions && (
        <TableSectionValuesPerForecast
          title={l('_DiscountFactor')}
          renderSpacer={false}
          additionalTitleClassNames="table-primary__cell--strong"
          renderCell={() => <CellValue value={' '} />}
        />
      )}
      <ValuationMovement
        instrument={instrument}
        transactionsByCaseAndYear={infusionTransactions}
        transactionType={TransactionType.Infusion}
        rowLabel={hasMultipleInfusionTransactions ? l('_Infusion') : l('_DiscountFactor')}
        valueSelector={(transaction) => transaction?.discountFactor}
        cellValueFormattingRules={decimalCalculatedViewRules}
      />
      <TableSectionValuesPerForecast
        title={l('_TotalPvOfInfusions')}
        additionalTitleClassNames="table-primary__cell--strong"
        renderSpacer={false}
        rowTooltipInfo={l('_InterimCashFlowMovementTooltip')}
        renderCell={({ forecast, caseItem }) => getTotalPvOfInfusionsValueCell(forecast, caseItem)}
      />
      <TableSpacerRow />
      <TotalPvOfInterimCashFlowsModal
        isOpen={modalHandler.isOpen}
        onClose={modalHandler.closeModal}
        transactions={modalHandler.transactions}
        getTransactionTypeLabel={modalHandler.getTransactionTypeLabel}
        isTransactionCashPaidOrDividend={modalHandler.isTransactionCashPaidOrDividend}
        tableTitle={modalHandler.tableTitle}
        totalPvValues={modalHandler.totalPvValues}
      />
    </>
  );
};
